// @file Surface-specific strategies for AI chat (board vs whiteboard).
// Consumed by surface_ai_chat_store — use the store, not this composable directly.
import type { WidgetType } from '@@/bits/surface_ai_chat_widgets'
import { useSurfaceStore } from '@@/pinia/surface'
import type { Suggestion } from '@@/pinia/surface_ai_chat_store'
import type { ComputedRef } from 'vue'
import { useBoardAIChatSurfaceStrategy } from './useBoardAIChatSurfaceStrategy'
import { useWhiteboardAIChatSurfaceStrategy } from './useWhiteboardAIChatSurfaceStrategy'

type AIChatSurfaceType = 'board' | 'whiteboard'

interface AIChatIntroductionConfig {
  title: string
  description: string
  manualMessagePlaceholder: string
}

interface AIChatPersonalizedTool {
  widgetType: WidgetType
  icon: string
  text: string
  message: string
  eventProps?: Record<string, string>
}

interface AIChatSurfaceStrategyReturn {
  surfaceType: AIChatSurfaceType
  xAiChat: ComputedRef<boolean>
  introduction: AIChatIntroductionConfig
  fallbackSuggestions: Suggestion[]
  shouldFetchSuggestionsFromApi: ComputedRef<boolean>
  personalizedTools: ComputedRef<AIChatPersonalizedTool[]>
}

function useAIChatSurfaceStrategy(): { aiChatSurfaceStrategy: AIChatSurfaceStrategyReturn } {
  const surfaceStore = useSurfaceStore()

  const aiChatSurfaceStrategy: AIChatSurfaceStrategyReturn = surfaceStore.isWhiteboard
    ? useWhiteboardAIChatSurfaceStrategy()
    : useBoardAIChatSurfaceStrategy()

  return { aiChatSurfaceStrategy }
}

export { useAIChatSurfaceStrategy }
export type { AIChatIntroductionConfig, AIChatPersonalizedTool, AIChatSurfaceStrategyReturn, AIChatSurfaceType }
